import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import portal from "../../../services/v2/portalAlumno.service";
import { AlumnoLista, apiAlumno, apiTareasAlumno, Materiales, PortalApiAlumno, PublicacionPortal, ResumenClase } from "../../../services/v2/classPortal.service";
import { abrirAulaEnPestana, mensajeDeAula } from "../../../utils/abrirAula";
import useEsTelefono from "../../../utils/esTelefono";
import {
	Avatar, fondoDePortada, Icono, mensajeDeError, PatronSvg, RedactorAlumno, TarjetaPublicacion, useAlVolver
} from "../portalClase/comun";
import { DetalleDeTarea, ListaDeTareasAlumno } from "../portalClase/tareas/tareasAlumno";
import { apiQuizAlumno } from "../../../services/v2/lmsQuiz.service";
import MisNotasPanel from "../portalClase/notas/misNotas";
import "../portalClase/portalClase.css";

/*
	Mi Clase (portal del alumno, Parte A, 2026-10-17): /miClase?h=<horario>. Clase (portada, próxima clase, anuncios, materiales
	nuevos, compañeros) y Materiales. En el celular la barra de abajo cambia de pestaña; en escritorio, las mismas pantallas
	centradas (máx. 720 px). El alumno sale de la sesión: aquí nunca se manda un id de alumno. El saldo vencido NO bloquea el
	portal, solo "Entrar A Clase". Carga al abrir y al volver a la pestaña; sin intervalos. Tareas (Parte B) y Mis Notas (Parte C).
*/
type Pestana = "clase" | "tareas" | "notas" | "materiales"
const PESTANAS: Pestana[] = ["clase", "tareas", "notas", "materiales"]

const MiClase: React.FC = () => {
	const [params, setParams] = useSearchParams()
	const h = Number(params.get("h")) || 0
	const t = Number(params.get("t")) || null
	const api: PortalApiAlumno = useMemo(() => apiAlumno(h), [h])
	const apiT = useMemo(() => apiTareasAlumno(h), [h])
	const apiQ = useMemo(() => apiQuizAlumno(h), [h])
	const navigate = useNavigate()
	const telefono = useEsTelefono()
	const [res, setRes] = useState<ResumenClase | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [pestana, setPestana] = useState<Pestana>(PESTANAS.includes(params.get("p") as Pestana) ? params.get("p") as Pestana : "clase")
	const [versionTareas, setVersionTareas] = useState<number>(0)
	const [posts, setPosts] = useState<PublicacionPortal[] | null>(null)
	const [mat, setMat] = useState<Materiales | null>(null)
	const [companeros, setCompaneros] = useState<{ visible: boolean; students: AlumnoLista[] } | null>(null)
	const [entrando, setEntrando] = useState<boolean>(false)
	const [aviso, setAviso] = useState<string | null>(null)
	const [desfase, setDesfase] = useState<number>(0)

	const cargar = useCallback(() => {
		if (!h) { setError("No encontramos esta clase."); return }
		// Si una recarga de fondo falla (al volver a la pestaña), la clase ya abierta se queda y solo sale un aviso chico arriba.
		api.overview().then((r) => { setRes(r); setError(null); setDesfase(r.serverNowMs ? r.serverNowMs - Date.now() : 0) })
			.catch((e) => setError(mensajeDeError(e, "No pudimos abrir esta clase.")))
		api.wall(1).then((m) => setPosts(m.posts)).catch(() => setPosts((p) => p || []))
		api.classmates().then(setCompaneros).catch(() => setCompaneros({ visible: false, students: [] }))
	}, [api, h])
	const cargarMateriales = useCallback(() => { api.materials().then(setMat).catch(() => setMat({ visible: true, units: [], posts: [] })) }, [api])

	useEffect(() => { cargar() }, [cargar])
	useEffect(() => { if (pestana === "materiales") cargarMateriales() }, [pestana, cargarMateriales])
	useAlVolver(useCallback(() => { cargar(); if (pestana === "materiales") cargarMateriales() }, [cargar, cargarMateriales, pestana]))

	if (error && res === null) return <div className="pc"><div className="pcPagina pcPaginaAlumno"><button type="button" className="pcEnlaceBtn" onClick={() => navigate("/misClases")}>Mis Clases</button><div className="pcAviso pcOro" role="alert">{error}</div></div></div>
	if (res === null) return <div className="pc"><div className="pcCargando">Cargando…</div></div>

	if (t !== null) {
		return <DetalleDeTarea apiT={apiT} api={api} apiQ={apiQ} postId={t} onVolver={() => { setVersionTareas((v) => v + 1); setParams({ h: String(h), p: params.get("de") === "notas" ? "notas" : "tareas" }) }} onCambio={() => { cargar(); setVersionTareas((v) => v + 1) }} />
	}

	const a = res.settings
	const pc = res.nextClass
	const ahora = Date.now() + desfase
	const aulaAbierta = !!pc && res.hasAula && ahora >= pc.opensAtMs && ahora <= pc.closesAtMs
	const porAbrir = !!pc && res.hasAula && ahora < pc.opensAtMs && pc.isToday
	const entrar = async () => {
		setAviso(null); setEntrando(true)
		try { await abrirAulaEnPestana(async () => (await portal.joinClass(h)).data.url) } catch (e) { setAviso(mensajeDeAula(e)) } finally { setEntrando(false) }
	}

	const reemplazar = (p: PublicacionPortal) => { setPosts((l) => (l || []).map((x) => (x.id === p.id ? p : x))); setMat((m) => (m ? { ...m, posts: m.posts.map((x) => (x.id === p.id ? p : x)) } : m)) }
	const quitar = (id: number) => { setPosts((l) => (l || []).filter((x) => x.id !== id)); setMat((m) => (m ? { ...m, posts: m.posts.filter((x) => x.id !== id) } : m)) }
	const puedeComentar = a.wallMode !== "maestro"
	const tarjeta = (p: PublicacionPortal, compacta = false) => (
		<TarjetaPublicacion key={p.id} api={api} post={p} esAlumno puedeComentar={puedeComentar} compacta={compacta} onCambio={reemplazar} onQuitar={quitar}
			onAbrirTarea={(x) => setParams({ h: String(h), t: String(x.id) })} />
	)
	// Las encuestas (Parte D) van con los anuncios.
	const anuncios = (posts || []).filter((p) => p.type === "anuncio" || p.type === "encuesta")
	const nuevosMateriales = (posts || []).filter((p) => p.type === "material").slice(0, 3)
	// Contenido que baja del curso (Parte A2): los Pendientes se marcan como hechos; las Tareas se ven con su ficha (la entrega llega en la Parte B).
	const pendientes = (posts || []).filter((p) => p.type === "pendiente")
	const tareas = (posts || []).filter((p) => p.type === "tarea")

	const tareasTab = <ListaDeTareasAlumno key={versionTareas} apiT={apiT} onAbrir={(id) => setParams({ h: String(h), t: String(id) })} />

	const claseTab = (
		<>
			<section className="pcTarjeta pcProxima" aria-label="Próxima clase" style={{ padding: 16 }}>
				{pc ? (
					<>
						<div className="pcProximaFila">
							<div className="pcFecha"><span>{pc.dayShort}</span><span>{pc.dayNumber}</span></div>
							<div style={{ flex: 1, minWidth: 0 }}>
								<div className="pcEtiqueta" style={{ fontSize: 12 }}>Próxima Clase</div>
								<div style={{ fontWeight: 700, fontSize: 16 }}>{pc.range}</div>
								<div className="pcTenue pcChico">{res.hasAula ? "Aula Virtual" + (porAbrir ? " · Abre " + pc.opensLabel : "") : [res.classroom, res.campus].filter(Boolean).join(" · ")}</div>
							</div>
						</div>
						{res.hasAula && (aulaAbierta || porAbrir) ? (
							<button type="button" className="pcBtn pcBtnP pcBtnBlock" style={{ minHeight: 48 }} disabled={!aulaAbierta || res.blocksAula || entrando} onClick={entrar}>Entrar A Clase</button>
						) : null}
						{res.hasAula && (aulaAbierta || porAbrir) && res.blocksAula ? <div className="pcNota pcNotaOro">Tienes un saldo vencido. Reporta tu pago o pide un pase en Caja.</div> : null}
						{res.hasAula && !res.blocksAula && res.passLabel ? <div className="pcNota">Pase hasta el {res.passLabel}</div> : null}
						{aviso ? <div className="pcNota pcNotaOro" role="alert">{aviso}</div> : null}
					</>
				) : <div className="pcVacio">Sin Clases Próximas</div>}
			</section>

			{a.welcome ? <div className="pcTarjeta" style={{ fontSize: 14.5, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{a.welcome}</div> : null}

			{a.wallMode === "publican" ? <RedactorAlumno api={api} yo={null} onPublicado={() => cargar()} /> : null}

			<div className="pcSeccion">Anuncios</div>
			{posts === null ? <div className="pcCargando">Cargando…</div> : anuncios.length === 0 ? <div className="pcVacio">Aún No Hay Anuncios</div> : anuncios.map((p) => tarjeta(p))}

			{pendientes.length > 0 ? (
				<>
					<div className="pcSeccion">Pendientes</div>
					{pendientes.map((p) => tarjeta(p, true))}
				</>
			) : null}

			{tareas.length > 0 ? (
				<>
					<div className="pcSeccion">Tareas</div>
					{tareas.map((p) => tarjeta(p, true))}
				</>
			) : null}

			{a.showMaterials && nuevosMateriales.length > 0 ? (
				<>
					<div className="pcSeccion">Materiales Nuevos</div>
					{nuevosMateriales.map((p) => tarjeta(p, true))}
					<button type="button" className="pcEnlaceBtn" onClick={() => setPestana("materiales")}>Ver Todos Los Materiales</button>
				</>
			) : null}

			{companeros?.visible && companeros.students.length > 0 ? (
				<>
					<div className="pcSeccion">Compañeros</div>
					<div className="pcAlumnos" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
						{companeros.students.map((s, i) => <div className="pcAlumno" key={i}><Avatar a={{ ...s, isTeacher: false }} size={32} />{s.name}{s.me ? " (Tú)" : ""}</div>)}
					</div>
				</>
			) : null}
		</>
	)

	const materialesTab = mat === null ? <div className="pcCargando">Cargando…</div> : !mat.visible ? <div className="pcVacio">Tu Maestro No Tiene Materiales Visibles</div> : (
		<>
			{mat.units.map((u) => {
				const ms = mat.posts.filter((p) => p.unit?.id === u.id)
				return ms.length === 0 ? null : (
					<section key={u.id} className="pcGrupo" aria-label={u.name}><div className="pcSeccion">{u.name}</div>{ms.map((p) => tarjeta(p, true))}</section>
				)
			})}
			{(() => {
				const sueltos = mat.posts.filter((p) => !p.unit)
				if (sueltos.length === 0) return mat.posts.length === 0 ? <div className="pcVacio">Aún No Hay Materiales</div> : null
				return <section className="pcGrupo" aria-label="Sin unidad">{mat.units.length > 0 ? <div className="pcSeccion">Otros Materiales</div> : null}{sueltos.map((p) => tarjeta(p, true))}</section>
			})()}
		</>
	)

	return (
		<div className="pc">
			<div className="pcPagina pcPaginaAlumno">
				<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<button type="button" className="pcIb" style={{ marginLeft: -10 }} aria-label="Mis Clases" onClick={() => navigate("/misClases")}><Icono n="atras" size={22} /></button>
					<span style={{ flex: 1, fontWeight: 700, fontSize: 15 }}>Mis Clases</span>
				</div>
				{error ? <div className="pcNota pcNotaOro" role="status">No pudimos actualizar la clase. Lo que ves puede no estar al día.</div> : null}

				<section aria-label="Portada" className="pcHero pcHeroAlumno" style={fondoDePortada(a)}>
					{a.coverPhotoUrl ? <div className="pcHeroSombra" /> : null}
					<PatronSvg patron={a.coverPattern} />
					<div className="pcHeroChips">
						<span className="pcChip">{res.modality === "virtual" ? "Virtual" : "Presencial"}</span>
						{res.unit ? <span className="pcChip">Unidad {res.unit.index} De {res.unit.total}</span> : null}
					</div>
					<div>
						<h1 className="pcHeroTitulo">{res.title}</h1>
						{res.period ? <div className="pcHeroPeriodo">{res.period}</div> : null}
					</div>
					<div className="pcHeroDatos" style={{ fontSize: 13.5, fontWeight: 600 }}>{[res.scheduleLabel, res.mentor].filter(Boolean).join(" \u00b7\u00a0")}</div>
				</section>

				{!telefono ? (
					<nav aria-label="Secciones de la clase" className="pcTabs">
						<button type="button" className={"pcTab" + (pestana === "clase" ? " pcTabOn" : "")} onClick={() => setPestana("clase")}>Clase</button>
						<button type="button" className={"pcTab" + (pestana === "tareas" ? " pcTabOn" : "")} onClick={() => setPestana("tareas")}>Tareas</button>
						<button type="button" className={"pcTab" + (pestana === "notas" ? " pcTabOn" : "")} onClick={() => setPestana("notas")}>Notas</button>
						<button type="button" className={"pcTab" + (pestana === "materiales" ? " pcTabOn" : "")} onClick={() => setPestana("materiales")}>Materiales</button>
					</nav>
				) : null}

				{pestana === "clase" ? claseTab : pestana === "tareas" ? tareasTab : pestana === "notas"
					? <MisNotasPanel h={h} onAbrirTarea={(id) => setParams({ h: String(h), t: String(id), de: "notas" })} /> : materialesTab}
			</div>

			{telefono ? (
				<nav aria-label="Secciones" className="pcBarraInferior">
					<button type="button" className={pestana === "clase" ? "pcNavOn" : ""} aria-current={pestana === "clase" ? "page" : undefined} onClick={() => setPestana("clase")}><Icono n="casa" size={22} />Clase</button>
					<button type="button" className={pestana === "tareas" ? "pcNavOn" : ""} aria-current={pestana === "tareas" ? "page" : undefined} onClick={() => setPestana("tareas")}><Icono n="clip" size={22} />Tareas</button>
					<button type="button" className={pestana === "notas" ? "pcNavOn" : ""} aria-current={pestana === "notas" ? "page" : undefined} onClick={() => setPestana("notas")}><Icono n="barras" size={22} />Notas</button>
					<button type="button" className={pestana === "materiales" ? "pcNavOn" : ""} aria-current={pestana === "materiales" ? "page" : undefined} onClick={() => setPestana("materiales")}><Icono n="doc" size={22} />Materiales</button>
				</nav>
			) : null}
		</div>
	)
}

export default MiClase
